import React from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { format } from 'date-fns';
import { Mail, Calendar, Trash2, ExternalLink } from 'lucide-react';

export const TodoDetail = ({ isOpen, onClose, todo, onDelete }) => {
  if (!todo) return null;

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Todo Details"
      size="lg"
    >
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-start justify-between mb-3">
            <h2 className="text-2xl font-semibold text-slate-800">
              {todo.title}
            </h2>
            <div className="flex gap-2">
              {todo.priority && (
                <Badge variant={getPriorityColor(todo.priority)}>
                  {todo.priority}
                </Badge>
              )}
              <Badge variant={getStatusColor(todo.status)}>
                {todo.status}
              </Badge>
            </div>
          </div>

          {todo.description && (
            <p className="text-slate-600">
              {todo.description}
            </p>
          )}
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
          <div>
            <span className="text-sm text-slate-600">Created</span>
            <p className="font-medium text-slate-800">
              {todo.created_at ? format(new Date(todo.created_at), 'MMM d, yyyy h:mm a') : 'N/A'}
            </p>
          </div>
          {todo.due_date && (
            <div>
              <span className="text-sm text-slate-600">Due Date</span>
              <p className="font-medium text-slate-800">
                {format(new Date(todo.due_date), 'MMM d, yyyy')}
              </p>
            </div>
          )}
          {todo.completed_at && (
            <div>
              <span className="text-sm text-slate-600">Completed</span>
              <p className="font-medium text-slate-800">
                {format(new Date(todo.completed_at), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
          )}
        </div>

        {/* Email Context */}
        {todo.email_context && (
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="text-periwinkle-500" size={20} />
              <h3 className="font-semibold text-slate-800">Email Context</h3>
            </div>
            <div className="space-y-2 text-sm">
              {todo.email_context.from && (
                <div>
                  <span className="text-slate-600">From: </span>
                  <span className="text-slate-800">{todo.email_context.from}</span>
                </div>
              )}
              {todo.email_context.subject && (
                <div>
                  <span className="text-slate-600">Subject: </span>
                  <span className="text-slate-800">{todo.email_context.subject}</span>
                </div>
              )}
              {todo.email_context.received_at && (
                <div>
                  <span className="text-slate-600">Received: </span>
                  <span className="text-slate-800">
                    {format(new Date(todo.email_context.received_at), 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
              )}
              {todo.email_context.gmail_link && (
                <a
                  href={todo.email_context.gmail_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-periwinkle-500 hover:text-periwinkle-600 mt-2"
                >
                  View Original Email
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Agent Execution Info */}
        {todo.agent_execution_id && (
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="text-mint-500" size={20} />
              <h3 className="font-semibold text-slate-800">Agent Execution</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-slate-600">Execution ID: </span>
                <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                  {todo.agent_execution_id}
                </code>
              </div>
              {todo.execution_plan && (
                <div>
                  <span className="text-slate-600">Plan: </span>
                  <p className="text-slate-800 mt-1">{todo.execution_plan}</p>
                </div>
              )}
              {todo.approved_at && (
                <div>
                  <Badge variant="success">Approved</Badge>
                  <span className="text-slate-600 ml-2">
                    {format(new Date(todo.approved_at), 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t border-slate-200">
          <Button
            variant="danger"
            onClick={() => {
              onDelete(todo.id);
              onClose();
            }}
            className="flex items-center gap-2"
          >
            <Trash2 size={18} />
            Delete Todo
          </Button>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
